import { useEffect, useMemo, useState } from "react";
import { api, TYPE_LABELS, money } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Calculator, RefreshCw, Check, Save, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function WalmartMargins() {
  const [rows, setRows] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [econError, setEconError] = useState(null);
  const [pfConfigured, setPfConfigured] = useState(true);

  // Editable assumptions (persisted via /walmart/settings)
  const [referral, setReferral] = useState(15);
  const [minMargin, setMinMargin] = useState(8);
  const [target, setTarget] = useState(30);
  const [absorb, setAbsorb] = useState(true);
  const [buffer, setBuffer] = useState(5);
  const [belowOnly, setBelowOnly] = useState(false);
  const [edits, setEdits] = useState({}); // id -> walmart_price string being typed

  const load = async (refresh = false) => {
    const { data } = await api.get(`/walmart/margins${refresh ? "?refresh=true" : ""}`);
    setRows(data.rows || []);
    setDefaults(data.defaults);
    setEconError(data.econ_error || null);
    setPfConfigured(!!data.printify_configured);
    if (data.defaults) {
      setReferral(data.defaults.referral_pct);
      setMinMargin(data.defaults.min_margin);
      setTarget(data.defaults.target_margin);
      setAbsorb(!!data.defaults.absorb_shipping);
      if (data.defaults.buffer_pct != null) setBuffer(data.defaults.buffer_pct);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const refreshCosts = async () => {
    setRefreshing(true);
    try { await load(true); toast.success("Live Printify costs refreshed"); }
    finally { setRefreshing(false); }
  };

  const saveDefaults = async () => {
    setSaving(true);
    try {
      await api.post("/walmart/settings", {
        referral_pct: Number(referral), min_margin: Number(minMargin),
        target_margin: Number(target), absorb_shipping: absorb, buffer_pct: Number(buffer),
      });
      toast.success("Walmart assumptions saved");
    } finally { setSaving(false); }
  };

  const fee = Number(referral) / 100;
  const tFrac = Number(target) / 100;
  const bufFrac = Number(buffer) / 100;

  const calc = (r) => {
    const price = Number(edits[r.id] ?? r.walmart_price) || 0;
    const ship = absorb ? Number(r.ship_first || 0) : 0;
    const bufferAmt = Number(r.unit_cost || 0) * bufFrac;
    const cost = Number(r.unit_cost || 0) + ship + bufferAmt;
    const referralFee = price * fee;
    const profit = price * (1 - fee) - cost;
    const margin = price > 0 ? (profit / price) * 100 : 0;
    const denom = 1 - fee - tFrac;
    const suggested = denom > 0 ? cost / denom : null; // price to hit target margin (shipping + buffer baked in)
    const eligible = price > 0 && margin >= Number(minMargin);
    return { price, ship, bufferAmt, cost, referralFee, profit, margin, suggested, eligible };
  };

  const computed = useMemo(() => rows.map((r) => ({ r, c: calc(r) })), [rows, edits, referral, minMargin, target, absorb, buffer]);
  const view = belowOnly ? computed.filter(({ c }) => !c.eligible) : computed;
  const belowCount = computed.filter(({ c }) => !c.eligible).length;
  const avgMargin = computed.length ? computed.reduce((s, { c }) => s + c.margin, 0) / computed.length : 0;

  const applyOne = async (r, price) => {
    if (!(price > 0)) return toast.error("Enter a price above 0");
    try {
      await api.post("/walmart/apply-prices", { items: [{ id: r.id, walmart_price: Number(price) }] });
      toast.success("Walmart price saved");
      await load();
      setEdits((e) => { const n = { ...e }; delete n[r.id]; return n; });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const applySuggestedBelow = async () => {
    const items = computed.filter(({ c }) => !c.eligible && c.suggested > 0)
      .map(({ r, c }) => ({ id: r.id, walmart_price: Math.round(c.suggested * 100) / 100 }));
    if (!items.length) return toast.error("No below-floor items with a valid suggested price");
    if (!window.confirm(`Set the suggested Walmart price on ${items.length} below-floor product(s)? This updates the Walmart list price only (planning field — nothing is pushed to Shopify/Walmart).`)) return;
    try {
      const { data } = await api.post("/walmart/apply-prices", { items });
      toast.success(`Updated ${data.updated} product(s)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="walmart-margins-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-blue-600 flex items-center gap-1.5"><Calculator className="w-3.5 h-3.5" /> Walmart.com · B2C</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Walmart margins</h1>
          <p className="text-sm text-zinc-500 mt-1">Per-item Walmart economics off your <b>commercial list price</b>. Walmart's referral fee already includes payment processing, so the only fee is the referral %.</p>
        </div>
        <button onClick={refreshCosts} disabled={refreshing} data-testid="refresh-walmart-costs-btn" className="h-9 px-3 rounded-md border border-zinc-300 hover:bg-zinc-50 text-sm font-medium flex items-center gap-1.5">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh live costs
        </button>
      </div>

      {!pfConfigured && <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">Printify isn't configured — costs shown are stored estimates.</div>}
      {econError && <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">{econError}</div>}

      {absorb && (
        <div className="mb-4 rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800" data-testid="walmart-freeship-note">
          <b>Free-shipping mode is ON.</b> Shipping + buffer are baked into the margin, so you can list these with <b>free domestic shipping</b> as a Walmart incentive. A green <b>OK</b> row means you can already afford free shipping <b>at your current price — no price change needed</b>. Only rows below your {minMargin}% floor need the suggested price (which raises it just enough to cover shipping).
        </div>
      )}

      {/* Assumptions */}
      <div className="mb-4 rounded-xl border border-zinc-200 bg-white p-4 flex flex-wrap items-end gap-4" data-testid="walmart-assumptions">
        <label className="block"><span className="text-[10px] text-zinc-400 block mb-1">Referral fee %</span>
          <Input type="number" min="0" value={referral} onChange={(e) => setReferral(e.target.value)} className="h-9 w-24 text-sm" data-testid="walmart-referral-input" /></label>
        <label className="block"><span className="text-[10px] text-zinc-400 block mb-1">Min margin % (floor)</span>
          <Input type="number" min="0" value={minMargin} onChange={(e) => setMinMargin(e.target.value)} className="h-9 w-28 text-sm" data-testid="walmart-minmargin-input" /></label>
        <label className="block"><span className="text-[10px] text-zinc-400 block mb-1">Target margin %</span>
          <Input type="number" min="0" value={target} onChange={(e) => setTarget(e.target.value)} className="h-9 w-24 text-sm" data-testid="walmart-target-input" /></label>
        <label className="block"><span className="text-[10px] text-zinc-400 block mb-1">Returns/ads buffer %</span>
          <Input type="number" min="0" value={buffer} onChange={(e) => setBuffer(e.target.value)} className="h-9 w-28 text-sm" data-testid="walmart-buffer-input" /></label>
        <div className="block"><span className="text-[10px] text-zinc-400 block mb-1">Shipping paid by</span>
          <div className="flex rounded-md border border-zinc-300 overflow-hidden text-xs" data-testid="walmart-ship-toggle">
            <button onClick={() => setAbsorb(true)} className={`px-3 h-9 ${absorb ? "bg-blue-600 text-white" : "bg-white text-zinc-600"}`}>Brand absorbs</button>
            <button onClick={() => setAbsorb(false)} className={`px-3 h-9 ${!absorb ? "bg-blue-600 text-white" : "bg-white text-zinc-600"}`}>Customer pays</button>
          </div>
        </div>
        <button onClick={saveDefaults} disabled={saving} data-testid="save-walmart-settings-btn" className="h-9 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium flex items-center gap-1.5">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save defaults
        </button>
      </div>

      {/* Summary */}
      <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="walmart-summary">
        <div className="rounded-lg border border-zinc-200 bg-white p-3"><div className="text-[10px] text-zinc-400">Products</div><div className="text-xl font-bold">{computed.length}</div></div>
        <div className="rounded-lg border border-zinc-200 bg-white p-3"><div className="text-[10px] text-zinc-400">Avg Walmart margin</div><div className={`text-xl font-bold ${avgMargin >= Number(minMargin) ? "text-emerald-600" : "text-rose-600"}`}>{avgMargin.toFixed(1)}%</div></div>
        <div className="rounded-lg border border-zinc-200 bg-white p-3"><div className="text-[10px] text-zinc-400">Below {minMargin}% floor</div><div className={`text-xl font-bold ${belowCount ? "text-rose-600" : "text-emerald-600"}`}>{belowCount}</div></div>
        <div className="rounded-lg border border-zinc-200 bg-white p-3 flex items-center justify-between">
          <div><div className="text-[10px] text-zinc-400">Show below-floor only</div>
            <button onClick={() => setBelowOnly((v) => !v)} data-testid="walmart-below-toggle" className={`mt-1 text-xs font-medium px-2 py-1 rounded ${belowOnly ? "bg-rose-600 text-white" : "bg-zinc-100 text-zinc-600"}`}>{belowOnly ? "On" : "Off"}</button>
          </div>
          {belowCount > 0 && <button onClick={applySuggestedBelow} data-testid="apply-walmart-suggested-btn" className="text-xs font-medium text-blue-600 hover:text-blue-700">Apply suggested to {belowCount}</button>}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-3 py-2.5">Product</th>
              <th className="text-right font-medium px-3 py-2.5">Walmart price</th>
              <th className="text-right font-medium px-3 py-2.5">Retail</th>
              <th className="text-right font-medium px-3 py-2.5">Unit cost{absorb ? " +ship" : ""}</th>
              <th className="text-right font-medium px-3 py-2.5">Referral 15%</th>
              <th className="text-right font-medium px-3 py-2.5">Unit profit</th>
              <th className="text-right font-medium px-3 py-2.5">Margin</th>
              <th className="text-left font-medium px-3 py-2.5">Walmart</th>
              <th className="text-right font-medium px-3 py-2.5">Suggested</th>
            </tr>
          </thead>
          <tbody>
            {view.map(({ r, c }) => (
              <tr key={r.id} data-testid={`walmart-row-${r.id}`} className={`border-t border-zinc-100 ${!c.eligible ? "bg-rose-50/40" : ""}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-zinc-800 truncate max-w-[280px]">{r.title}</div>
                  <div className="text-[11px] text-zinc-400">{TYPE_LABELS[r.product_type] || r.product_type}{r.cost_is_estimate ? " · est. cost" : ""}</div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1 justify-end">
                    <span className="text-zinc-400 text-xs">$</span>
                    <Input type="number" min="0" value={edits[r.id] ?? r.walmart_price} onChange={(e) => setEdits((s) => ({ ...s, [r.id]: e.target.value }))}
                      className={`h-8 w-24 text-sm text-right ${!r.has_walmart_price ? "border-dashed" : ""}`} data-testid={`walmart-price-${r.id}`} />
                    {(edits[r.id] !== undefined && Number(edits[r.id]) !== r.walmart_price) && (
                      <button onClick={() => applyOne(r, edits[r.id])} data-testid={`walmart-save-${r.id}`} className="text-blue-600 hover:text-blue-700"><Check className="w-4 h-4" /></button>
                    )}
                  </div>
                  {!r.has_walmart_price && <div className="text-[10px] text-zinc-400">using retail</div>}
                </td>
                <td className="px-3 py-2 text-right text-zinc-500">{money(r.retail)}</td>
                <td className="px-3 py-2 text-right">
                  <div>{money(c.cost)}</div>
                  {(c.ship > 0 || c.bufferAmt > 0) && (
                    <div className="text-[10px] text-zinc-400" data-testid={`walmart-cost-breakdown-${r.id}`}>
                      {c.ship > 0 && <span>incl {money(c.ship)} ship</span>}
                      {c.ship > 0 && c.bufferAmt > 0 && " · "}
                      {c.bufferAmt > 0 && <span>{money(c.bufferAmt)} buffer</span>}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-zinc-500">{money(c.referralFee)}</td>
                <td className={`px-3 py-2 text-right font-medium ${c.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{money(c.profit)}</td>
                <td className={`px-3 py-2 text-right font-semibold ${c.margin >= Number(minMargin) ? "text-emerald-600" : "text-rose-600"}`}>{c.margin.toFixed(1)}%</td>
                <td className="px-3 py-2">
                  {c.eligible
                    ? <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">OK</span>
                    : <span className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {c.price <= 0 ? "no price" : `${c.margin.toFixed(0)}%`}</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {c.suggested ? (
                    <div className="inline-flex items-center gap-1.5">
                      <span className="text-zinc-600">{money(c.suggested)}</span>
                      <button onClick={() => applyOne(r, Math.round(c.suggested * 100) / 100)} data-testid={`walmart-apply-${r.id}`} className="text-[11px] text-blue-600 hover:text-blue-700 font-medium">Apply</button>
                    </div>
                  ) : <span className="text-[11px] text-zinc-400">n/a</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {view.length === 0 && <div className="p-8 text-center text-sm text-zinc-400">No products{belowOnly ? " below the floor" : ""}.</div>}
      </div>
      <p className="text-[11px] text-zinc-400 mt-3">Suggested price = the Walmart list price needed to hit your {target}% target margin at the current referral fee and shipping assumption. Walmart prices here are a Heartline planning field — nothing is pushed to Shopify or Walmart automatically.</p>
    </div>
  );
}
